import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import {
  communityOverviewResponseSchema,
  type CommunityOverviewResponse,
} from "@community-analytics/shared";

import { apiFetch } from "@shared/api";

import { communityQueryKeys } from "./keys";

/**
 * GET /api/communities/:id/overview — the Mini App "Overview" page payload.
 *
 * The hook is a no-op when `communityId` is null (e.g. before the
 * communities list resolves and the active-community store picks one) —
 * `enabled: false` keeps TanStack Query from firing a request with a
 * malformed URL.
 */
export function useOverviewQuery(
  communityId: string | null,
): UseQueryResult<CommunityOverviewResponse, Error> {
  return useQuery<CommunityOverviewResponse, Error>({
    queryKey: communityQueryKeys.overview(communityId ?? "__noop__"),
    enabled: communityId !== null,
    queryFn: async () => {
      // Re-asserted to satisfy the type-narrower; `enabled` already gates
      // the call so this branch is unreachable in production.
      if (communityId === null) throw new Error("communityId is required");
      const raw = await apiFetch<unknown>(
        `/api/communities/${encodeURIComponent(communityId)}/overview`,
      );
      return communityOverviewResponseSchema.parse(raw);
    },
  });
}
